import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { RecordingsCommunitiesStore } from '@appState/marketing/recordings/recordings-communities/recordings-communities.store';
import { Community, CommunityBase, CommunityListItem, CreateCommunityRequest, UpdateCommunityRequest } from '@models/communities';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { addEntity, createCustomStateSetOnUrlHandler, deleteEntity, getAll, getOne, StateDictionary, updateEntity } from '../helpers';
import { NewsCommunitiesStore } from '../news-articles/news-communities/news-communities.store';
import { CommunitiesService } from './communities.service';

const sortByName = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name);

type CommunitiesState = {
  isLoading: boolean;
  isCommunityCreateLoading: boolean;
  communities: CommunityListItem[];
  selectedCommunity: Community | null;
  isSelectedCommunityLoading: boolean;
  communitiesBase: CommunityBase[];
  isCommunitiesBaseLoading: boolean;
};

const initialState: CommunitiesState = {
  isLoading: false,
  isCommunityCreateLoading: false,
  communities: [],
  selectedCommunity: null,
  isSelectedCommunityLoading: false,
  communitiesBase: [],
  isCommunitiesBaseLoading: false,
};

const communitiesDictionary: StateDictionary<CommunityListItem> = {
  entityList: 'communities',
  listLoading: 'isLoading',
  selectedEntity: 'selectedCommunity',
  selectedEntityLoading: 'isSelectedCommunityLoading',
  entityId: 'id',
  createLoading: 'isCommunityCreateLoading',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};

export const CommunitiesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('communities-store'),
  withComputed(store => ({
    selectedCommunityId: computed(() => store.selectedCommunity()?.id || null),
  })),
  withMethods(
    (
      store,
      communitiesService = inject(CommunitiesService),
      router = inject(Router),
      newsCommunitiesStore = inject(NewsCommunitiesStore),
      recordingsCommunitiesStore = inject(RecordingsCommunitiesStore),
    ) => {
      createCustomStateSetOnUrlHandler<CommunitiesState>('/marketing/communities', { selectedCommunity: null, communitiesBase: [] })(store);
      createCustomStateSetOnUrlHandler<CommunitiesState>('/marketing/news-articles', { selectedCommunity: null })(store);

      const utilMethods = {
        clearCommunitiesFromOtherStores: (): void => {
          newsCommunitiesStore.resetState({ newsCommunities: [] });
          recordingsCommunitiesStore.resetState({ recordingsCommunities: [] });
        },
      };

      const repositoryMethods = {
        getAll: getAll<CommunityListItem>({
          store,
          service: communitiesService,
          dictionary: communitiesDictionary,
        }),

        getOne: getOne<Community>({
          store,
          service: communitiesService,
          dictionary: communitiesDictionary,
        }),

        addCommunity: addEntity<Community, CreateCommunityRequest>({
          store,
          service: communitiesService,
          dictionary: communitiesDictionary,
          onSuccessCallback: (community: Community) => {
            utilMethods.clearCommunitiesFromOtherStores();
            newsCommunitiesStore.getOne(community.id);
            router.navigate(['marketing', 'communities', community.id, 'details']);
          },
        }),

        updateCommunity: updateEntity<Community, UpdateCommunityRequest>({
          store,
          service: communitiesService,
          dictionary: communitiesDictionary,
        }),

        deleteCommunity: deleteEntity<Community>({
          store,
          service: communitiesService,
          dictionary: communitiesDictionary,
          onSuccessCallback: () => {
            router.navigate(['marketing', 'communities']);
            repositoryMethods.getAll();
            utilMethods.clearCommunitiesFromOtherStores();
          },
        }),

        getCommunitiesBase: rxMethod<void>(
          pipe(
            tap(() => patchState(store, { isCommunitiesBaseLoading: true })),
            switchMap(() =>
              communitiesService.getCommunitiesBase().pipe(
                tap(communities => {
                  patchState(store, {
                    communitiesBase: [...communities].sort(sortByName),
                    isCommunitiesBaseLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isCommunitiesBaseLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const navigationActions = {
        goToCommunityActions: async (id: string): Promise<boolean> => {
          const result = await router.navigate(['marketing', 'communities', id]);
          if (!result) return false;

          if (!store.isSelectedCommunityLoading()) repositoryMethods.getOne(id);
          return true;
        },

        goToListPage: (): void => {
          patchState(store, { selectedCommunity: null });
          router.navigate(['marketing', 'communities']);
        },
      };

      return {
        ...repositoryMethods,
        ...navigationActions,
        resetState: (state: Partial<CommunitiesState>) => patchState(store, state),
      };
    },
  ),
);

export interface CommunitiesStoreType {
  readonly isLoading: Signal<boolean>;
  readonly isCommunityCreateLoading: Signal<boolean>;
  readonly communities: Signal<CommunityListItem[]>;
  readonly selectedCommunity: Signal<Community | null>;
  readonly isSelectedCommunityLoading: Signal<boolean>;
  readonly communitiesBase: Signal<CommunityBase[]>;
  readonly isCommunitiesBaseLoading: Signal<boolean>;

  readonly selectedCommunityId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly addCommunity: (dto: CreateCommunityRequest) => void;
  readonly updateCommunity: (input: { id: string; dto: UpdateCommunityRequest }) => void;
  readonly deleteCommunity: (id: string) => void;
  readonly getCommunitiesBase: () => void;

  readonly goToCommunityActions: (id: string) => Promise<boolean>;
  readonly goToListPage: () => void;
  readonly resetState: (state: Partial<CommunitiesState>) => void;
}
